import React, { Suspense } from 'react'
import { Alert, AlertIcon, Divider, Stack, Skeleton } from '@chakra-ui/react'
import { RentalApplication } from '@homevest/types/rental-applications'
import { RENTAL_APPLICATION_STATUSES } from '@homevest/utils/rental-applications'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { CustomSpinner } from 'ui/Loading'
import { useRentalAppQualification } from './useRentalAppQualification'
import { ApplicantSummaryPanel } from './ApplicantSummaryPanel'
import DecisionPanel from './DecisionPanel'
import RecommendationPanel from './RecommendationPanel'

const { PENDING_CUSTOMER_INFORMATION, PENDING_APPLICATION_REVIEW } = RENTAL_APPLICATION_STATUSES

const UnderwritingDetailsInner = ({
  rentalApplication
}: {
  rentalApplication: RentalApplication
}) => {
  const { data, isFetching, error } = useRentalAppQualification(rentalApplication.id)

  const isCustomerInfo = rentalApplication.status === PENDING_CUSTOMER_INFORMATION
  const isReview = rentalApplication.status === PENDING_APPLICATION_REVIEW

  if (isCustomerInfo) {
    return null
  }

  // Display alert for unexpected errors
  if (error) {
    const errorMessage = error
      ? ((error as any)?.message as string) || JSON.stringify(error)
      : 'Underwriting details not loaded'

    return (
      <Alert status='error' variant='subtle'>
        <AlertIcon />
        Error loading underwriting details: {errorMessage}
      </Alert>
    )
  }

  if (isFetching || !data) {
    return <Skeleton height='100px' />
  }

  const decisionPanel = (
    <>
      <DecisionPanel rentalApplication={rentalApplication} qualificationResponse={data} />
      <Divider />
    </>
  )

  // Display info for expected errors
  if ('error' in data) {
    return (
      // In these cases we might still want underwriter to be able to approve
      // (especially for SafeRent applications - these will always error b/c database doesn't have credit score)
      <Stack spacing={4}>
        {!isReview && decisionPanel}
        <Alert status='info' variant='subtle'>
          <AlertIcon />
          {data.error}
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack gap={4}>
      {!isReview && decisionPanel}
      <RecommendationPanel qualificationDetails={data} />
      <ApplicantSummaryPanel qualificationDetails={data} />
    </Stack>
  )
}

const UnderwritingDetails = ({ rentalApplication }: { rentalApplication: RentalApplication }) => {
  const isCustomerInfo = rentalApplication.status === PENDING_CUSTOMER_INFORMATION
  const isReview = rentalApplication.status === PENDING_APPLICATION_REVIEW

  if (isCustomerInfo) {
    return null
  }

  return (
    <ContentSectionCard
      title='Underwriting Details'
      collapsable={isReview}
      isOpenDefault={!isReview}
      padding
    >
      <Suspense fallback={<CustomSpinner />}>
        <UnderwritingDetailsInner rentalApplication={rentalApplication} />
      </Suspense>
    </ContentSectionCard>
  )
}

export default UnderwritingDetails
